import { useState } from 'react';

// Clinic's WhatsApp number in international format, no + or spaces
const CLINIC_WHATSAPP_NUMBER = '6283876459';

const buildWhatsAppMessage = ({ name, age, query }) => {
  return (
    `Hello, I'd like to talk to a care guide.\n\n` +
    `*Name:* ${name}\n` +
    `*Age:* ${age}\n` +
    `*Query:* ${query}`
  );
};

const TalkToCareGuideModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: '', age: '', query: '' });
  const [errors, setErrors] = useState({});

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Name is required';
    if (!form.age.trim()) {
      next.age = 'Age is required';
    } else if (!/^\d{1,3}$/.test(form.age.trim())) {
      next.age = 'Enter a valid age';
    }
    if (!form.query.trim()) next.query = 'Please tell us how we can help';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const message = buildWhatsAppMessage({
      name: form.name.trim(),
      age: form.age.trim(),
      query: form.query.trim(),
    });

    const waUrl = `https://wa.me/${CLINIC_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;

    // Open WhatsApp in a new tab/app — user taps Send to complete it
    window.open(waUrl, '_blank', 'noopener,noreferrer');

    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full flex flex-col relative"
        style={{ maxWidth: 420, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #f0f0f0' }}>
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center rounded-full flex-shrink-0"
              style={{ width: 34, height: 34, backgroundColor: '#E4F7F1' }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#0F8C6E">
                <path d="M17.6 6.32A8.9 8.9 0 0 0 12.05 4C7.1 4 3.09 8 3.09 12.95c0 1.6.42 3.16 1.22 4.53L3 21l3.62-1.28a9.05 9.05 0 0 0 5.43 1.75c4.95 0 8.96-4 8.96-8.95 0-2.4-.93-4.65-2.6-6.35zM12.05 19.9a7.4 7.4 0 0 1-4.63-1.6l-.33-.24-2.16.77.72-2.11-.26-.36a7.36 7.36 0 0 1-1.4-4.4c0-4.08 3.32-7.4 7.4-7.4a7.35 7.35 0 0 1 7.4 7.4c0 4.08-3.32 7.4-7.4 7.4z" />
              </svg>
            </div>
            <div>
              <h3 className="ava-text-subheading" style={{ margin: 0 }}>Talk to a Care Guide</h3>
              <p className="ava-text-xs" style={{ margin: 0, color: '#6b7280' }}>We'll follow up over WhatsApp</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
            style={{ border: 'none', background: 'none', color: 'var(--text-light, #6b7280)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto px-5 py-4 flex flex-col gap-4">
          {/* Name */}
          <div className="flex flex-col gap-1">
            <label className="ava-text-xs" style={{ color: '#374151' }}>Full Name</label>
            <input
              type="text"
              placeholder="Your full name"
              value={form.name}
              onChange={handleChange('name')}
              className="w-full px-4 py-3 text-sm rounded-xl outline-none transition-colors"
              style={{ border: errors.name ? '1.5px solid #dc2626' : '1.5px solid #e5e7eb' }}
            />
            {errors.name && <p className="ava-text-xs" style={{ color: '#dc2626' }}>{errors.name}</p>}
          </div>

          {/* Age */}
          <div className="flex flex-col gap-1">
            <label className="ava-text-xs" style={{ color: '#374151' }}>Age</label>
            <input
              type="text"
              inputMode="numeric"
              placeholder="e.g. 32"
              value={form.age}
              maxLength={3}
              onChange={(e) => {
                setForm((prev) => ({ ...prev, age: e.target.value.replace(/\D/g, '') }));
                setErrors((prev) => ({ ...prev, age: '' }));
              }}
              className="w-full px-4 py-3 text-sm rounded-xl outline-none transition-colors"
              style={{ border: errors.age ? '1.5px solid #dc2626' : '1.5px solid #e5e7eb' }}
            />
            {errors.age && <p className="ava-text-xs" style={{ color: '#dc2626' }}>{errors.age}</p>}
          </div>

          {/* Query */}
          <div className="flex flex-col gap-1">
            <label className="ava-text-xs" style={{ color: '#374151' }}>Message / Query</label>
            <textarea
              placeholder="Tell us how we can help..."
              value={form.query}
              onChange={handleChange('query')}
              rows={4}
              className="w-full px-4 py-3 text-sm rounded-xl outline-none transition-colors resize-none"
              style={{ border: errors.query ? '1.5px solid #dc2626' : '1.5px solid #e5e7eb' }}
            />
            {errors.query && <p className="ava-text-xs" style={{ color: '#dc2626' }}>{errors.query}</p>}
          </div>
        </form>

        {/* Footer */}
        <div className="px-5 pt-3 pb-5 flex-shrink-0" style={{ borderTop: '1px solid #f0f0f0' }}>
          <button
            onClick={handleSubmit}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-opacity hover:opacity-90 cursor-pointer flex items-center justify-center gap-2"
            style={{ backgroundColor: '#0F8C6E', color: '#fff', border: 'none' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
              <path d="M17.6 6.32A8.9 8.9 0 0 0 12.05 4C7.1 4 3.09 8 3.09 12.95c0 1.6.42 3.16 1.22 4.53L3 21l3.62-1.28a9.05 9.05 0 0 0 5.43 1.75c4.95 0 8.96-4 8.96-8.95 0-2.4-.93-4.65-2.6-6.35zM12.05 19.9a7.4 7.4 0 0 1-4.63-1.6l-.33-.24-2.16.77.72-2.11-.26-.36a7.36 7.36 0 0 1-1.4-4.4c0-4.08 3.32-7.4 7.4-7.4a7.35 7.35 0 0 1 7.4 7.4c0 4.08-3.32 7.4-7.4 7.4z" />
            </svg>
            Send via WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── The button you drop anywhere in your page ── */
const TalkToCareGuideButton = ({ label = 'Talk to Care Guide', className = 'btn-primary' }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <button onClick={() => setShowModal(true)} className={className}>
        {label}
      </button>
      {showModal && <TalkToCareGuideModal onClose={() => setShowModal(false)} />}
    </>
  );
};

export default TalkToCareGuideButton;